import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../common/types';
import type { HeaderPicture } from './useHeaderPictures';
import { spotifyHeaderMessages as messages } from './spotifyHeaderMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Spotify header; each part uses its own slice of it */
import styles from './SpotifyHeader.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface HeaderPicturePickerProps {
  title: string;
  hint: string;
  pictures: HeaderPicture[];
  value: string | undefined;
  /** Shown when the list is empty. */
  empty: React.ReactNode;
  testId: string;
  onPick: (picture: HeaderPicture) => void;
}

/** The picker of a bank or an AI picture (design spotify-header a2): a grid of thumbnails, the pick has the ring. */
const HeaderPicturePickerComponent: React.FC<HeaderPicturePickerProps> = ({
  title,
  hint,
  pictures,
  value,
  empty,
  testId,
  onPick,
}) => {
  const intl = useIntl();
  return (
    <div className={styles.picker} data-testid={testId}>
      <div className={styles.pickerHead}>
        <span className={styles.pickerTitle}>{title}</span>
        <span className={styles.pickerHint}>{hint}</span>
      </div>
      {pictures.length === 0 ? (
        empty
      ) : (
        <div className={styles.grid} role="radiogroup" aria-label={title}>
          {pictures.map((picture, index) => {
            const isOn = picture.uuid === value;
            return (
              <button
                key={picture.uuid}
                type="button"
                role="radio"
                aria-checked={isOn}
                aria-label={intl.formatMessage(messages.pickPicture, { number: index + 1 })}
                className={clsx(styles.thumb, isOn && styles.thumbOn)}
                style={{ backgroundImage: `url("${picture.thumbUrl}")` }}
                data-testid={`${testId}-${picture.uuid}`}
                onClick={() => onPick(picture)}
              >
                {isOn && (
                  <span className={styles.check} aria-hidden>
                    ✓
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const HeaderPicturePicker = TypedMemo(HeaderPicturePickerComponent);
